import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import MobileNavigation from './MobileNavigation';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Shield, 
  LayoutDashboard, 
  AlertTriangle, 
  MessageSquare, 
  Users,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  User,
  Bell,
  GraduationCap,
  BarChart3,
  Stethoscope,
  History
} from 'lucide-react';

const NAV_ITEMS = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/panic', icon: AlertTriangle, label: 'Panic', highlight: true },
  { path: '/alerts', icon: Bell, label: 'Alerts' },
  { path: '/chat', icon: MessageSquare, label: 'Chat' },
  { path: '/team', icon: Users, label: 'Team' },
  { path: '/documents', icon: FileText, label: 'Docs' },
  { path: '/training', icon: GraduationCap, label: 'Training' },
  { path: '/reports', icon: BarChart3, label: 'Reports', permission: 'can_view_reports' },
  { path: '/diagnostics', icon: Stethoscope, label: 'Diagnostics', adminOnly: true },
  { path: '/audit-log', icon: History, label: 'Audit Log', adminOnly: true },
  { path: '/settings', icon: Settings, label: 'Settings' },
];

const Layout = ({ children }) => {
  const { user, logout, alerts, onlineUsers, hasPermission } = useAuth();
  const { settings, getLogoUrl } = useSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const activeAlerts = alerts.filter(a => a.status === 'active').length;
  const logoUrl = getLogoUrl();

  const visibleNavItems = NAV_ITEMS.filter((item) => {
    if (item.permission && !hasPermission(item.permission)) return false;
    if (item.adminOnly && !(hasPermission('can_manage_team') || user?.role === 'security_head')) return false;
    return true;
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-void grid-pattern">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-panel border-r border-white/10">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 py-5 border-b border-white/10">
            <div 
              className="w-10 h-10 rounded-sm flex items-center justify-center overflow-hidden"
              style={{ backgroundColor: `${settings.primary_color}20` }}
            >
              {logoUrl ? (
                <img 
                  src={logoUrl} 
                  alt={settings.app_title} 
                  className="w-full h-full object-contain p-1"
                />
              ) : (
                <Shield className="w-6 h-6" style={{ color: settings.primary_color }} />
              )}
            </div>
            <div>
              <h1 className="font-chivo font-bold text-sm uppercase tracking-tight text-white">
                {settings.app_title}
              </h1>
              <p className="font-mono text-[10px] text-muted-foreground uppercase tracking-wider">
                {settings.app_subtitle}
              </p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1">
            {visibleNavItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-colors ${
                    active 
                      ? 'bg-primary/20 text-primary' 
                      : item.highlight
                        ? 'text-alert-red hover:bg-alert-red/10'
                        : 'text-muted-foreground hover:text-white hover:bg-white/5'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-inter text-sm">{item.label}</span>
                  {item.path === '/alerts' && activeAlerts > 0 && (
                    <Badge className="ml-auto bg-alert-red text-white font-mono text-[10px] px-1.5">
                      {activeAlerts}
                    </Badge>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User Profile */}
          <div className="p-4 border-t border-white/10">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 w-full p-2 rounded-sm hover:bg-white/5 transition-colors">
                  <div className="w-8 h-8 rounded-sm bg-surface flex items-center justify-center">
                    <span className="font-chivo font-bold text-xs text-white">
                      {user?.name?.split(' ').map(n => n[0]).join('') || 'U'}
                    </span>
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <p className="font-inter text-sm text-white truncate">{user?.name}</p>
                    <p className="font-mono text-[10px] text-muted-foreground uppercase">
                      {user?.role?.replace(/_/g, ' ')}
                    </p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-panel border-white/10 rounded-sm">
                <DropdownMenuItem className="font-mono text-xs cursor-pointer">
                  <User className="w-4 h-4 mr-2" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem className="font-mono text-xs cursor-pointer">
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="font-mono text-xs cursor-pointer text-alert-red focus:text-alert-red"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-panel border-b border-white/10">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-primary" />
            <span className="font-chivo font-bold text-sm uppercase text-white">
              TCS
            </span>
          </div>
          <div className="flex items-center gap-2">
            {activeAlerts > 0 && (
              <Link to="/alerts">
                <Badge className="bg-alert-red text-white font-mono text-[10px]">
                  {activeAlerts} Active
                </Badge>
              </Link>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-white"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-panel border-b border-white/10 glass">
            <nav className="p-4 space-y-1">
              {visibleNavItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-3 py-3 rounded-sm ${
                      active 
                        ? 'bg-primary/20 text-primary' 
                        : item.highlight
                          ? 'text-alert-red'
                          : 'text-muted-foreground'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-inter text-sm">{item.label}</span>
                  </Link>
                );
              })}
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-3 py-3 rounded-sm text-alert-red w-full"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-inter text-sm">Logout</span>
              </button>
            </nav>
          </div>
        )}
      </header>

      {/* Mobile Bottom Nav - Now Movable */}
      <MobileNavigation activeAlerts={activeAlerts} />

      {/* Main Content */}
      <main className="lg:pl-64 pt-14 lg:pt-0 pb-20 lg:pb-0">
        {children}
      </main>
    </div>
  );
};

export default Layout;
